import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, ArrowLeft, Send, Plus, Trash2 } from "lucide-react";

export default function PortalReferenceUpdate() {
  const { cid } = useParams();
  const [verifyEmail, setVerifyEmail] = useState("");
  const [refs, setRefs] = useState([]);
  const [loadedEmail, setLoadedEmail] = useState(false);
  const [removeIdx, setRemoveIdx] = useState({});
  const [reasons, setReasons] = useState({});
  const [newRefs, setNewRefs] = useState([{ name: "", email: "", relationship: "manager", phone: "" }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Light "preview" — we ask the candidate to type their email; only then do we fetch their references via a public endpoint.
  const loadRefs = async () => {
    if (!verifyEmail.trim()) return toast.error("Please enter your email");
    try {
      const { data } = await api.get(`/portal/track`, { params: { email: verifyEmail.trim() } });
      const me = (data || []).find((c) => c.id === cid);
      if (!me) return toast.error("We could not find a matching application. Check the email used.");
      setRefs(me.references || []);
      setLoadedEmail(true);
      toast.success(`Loaded ${me.references?.length || 0} references on file.`);
    } catch { toast.error("Verification failed"); }
  };

  const submit = async () => {
    const removeList = Object.entries(removeIdx).filter(([, v]) => v).map(([k]) => ({ index: Number(k), name: refs[k]?.name, reason: reasons[k] || "not_responding" }));
    const newList = newRefs.filter((r) => r.name && r.email);
    if (removeList.length === 0 && newList.length === 0) return toast.error("Pick at least one ref to remove or add a new one");
    setBusy(true);
    try {
      await api.post(`/portal/candidates/${cid}/reference-update-request`, {
        verify_email: verifyEmail.trim(),
        references_to_remove: removeList,
        new_references: newList,
        candidate_notes: notes,
      });
      setDone(true);
      toast.success("Request submitted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cc-bg)" }}>
      <header className="cc-topbar px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/rabah-logo.png" alt="Rabah Concept" className="w-9 h-9 object-contain" />
          <div className="font-display font-bold">Rabah Concept</div>
        </Link>
        <Link to="/track" className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Track application</Link>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-eyebrow mb-2">Pre-employment checks</div>
        <h1 className="font-display text-4xl font-bold mb-2 flex items-center gap-3"><Users className="w-8 h-8" /> Update your references</h1>
        <p className="text-[var(--cc-text-soft)] mb-8">Use this form if one of your referees isn't responding. Our HR team will review your request within 2 business days.</p>

        {done ? (
          <div className="cc-card p-8 text-center">
            <div className="text-emerald-600 text-3xl mb-2">✓</div>
            <h2 className="font-display text-2xl font-bold mb-2">Request submitted</h2>
            <p className="text-[var(--cc-text-soft)] mb-5">We'll email you once our HR team has reviewed your request.</p>
            <Link to="/track" className="btn-primary">Back to tracker</Link>
          </div>
        ) : !loadedEmail ? (
          <div className="cc-card p-6 space-y-3" data-testid="ref-verify-card">
            <label className="label-eyebrow block">Confirm your email</label>
            <input type="email" className="cc-input" value={verifyEmail} onChange={(e) => setVerifyEmail(e.target.value)}
                   placeholder="the email you used to apply" data-testid="ref-verify-email" />
            <p className="text-xs text-[var(--cc-text-soft)]">For your protection, we'll re-verify your identity before showing your references.</p>
            <button className="btn-primary" onClick={loadRefs} data-testid="ref-verify-button">Continue</button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="ref-update-form">
            <div className="cc-card p-5">
              <div className="label-eyebrow mb-3">Your current references</div>
              {refs.length === 0 ? <div className="text-sm text-[var(--cc-text-soft)]">No references on file yet.</div> : (
                <div className="space-y-2">
                  {refs.map((r, i) => (
                    <label key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[var(--cc-border)] hover:bg-[var(--cc-surface)] cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={!!removeIdx[i]}
                             onChange={(e) => setRemoveIdx({ ...removeIdx, [i]: e.target.checked })}
                             data-testid={`ref-remove-${i}`} />
                      <div className="flex-1">
                        <div className="font-medium">{r.name || `Reference ${i + 1}`} <span className="text-xs text-[var(--cc-text-soft)]">({r.relationship || "—"})</span></div>
                        <div className="text-xs text-[var(--cc-text-soft)]">{r.email}{r.phone ? ` · ${r.phone}` : ""} · Status: {r.status || "pending"}</div>
                        {removeIdx[i] && (
                          <select className="cc-select mt-2 text-xs py-1" value={reasons[i] || "not_responding"} onChange={(e) => setReasons({ ...reasons, [i]: e.target.value })}>
                            <option value="not_responding">Not responding</option>
                            <option value="left_company">Referee left their company</option>
                            <option value="wrong_contact">Wrong contact details</option>
                            <option value="other">Other</option>
                          </select>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="cc-card p-5">
              <div className="label-eyebrow mb-3">Add new references</div>
              <div className="space-y-3">
                {newRefs.map((r, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 rounded-lg border border-[var(--cc-border)]" data-testid={`ref-new-${i}`}>
                    <input className="cc-input md:col-span-3" placeholder="Name" value={r.name} onChange={(e) => { const n=[...newRefs]; n[i].name=e.target.value; setNewRefs(n); }} />
                    <input className="cc-input md:col-span-4" placeholder="Email" type="email" value={r.email} onChange={(e) => { const n=[...newRefs]; n[i].email=e.target.value; setNewRefs(n); }} />
                    <select className="cc-select md:col-span-2" value={r.relationship} onChange={(e) => { const n=[...newRefs]; n[i].relationship=e.target.value; setNewRefs(n); }}>
                      <option value="manager">Manager</option>
                      <option value="colleague">Colleague</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="client">Client</option>
                      <option value="other">Other</option>
                    </select>
                    <input className="cc-input md:col-span-2" placeholder="Phone (optional)" value={r.phone} onChange={(e) => { const n=[...newRefs]; n[i].phone=e.target.value; setNewRefs(n); }} />
                    {newRefs.length > 1 && (
                      <button className="btn-ghost md:col-span-1" onClick={() => setNewRefs(newRefs.filter((_, j) => j !== i))} title="Remove" data-testid={`ref-new-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button className="btn-ghost" onClick={() => setNewRefs([...newRefs, { name: "", email: "", relationship: "manager", phone: "" }])} data-testid="ref-new-add">
                  <Plus className="w-4 h-4" /> Add another reference
                </button>
              </div>
            </div>

            <div className="cc-card p-5">
              <label className="label-eyebrow block mb-2">Why do you need these changes? (optional)</label>
              <textarea className="cc-textarea min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Original manager has left the company and isn't reachable." />
            </div>

            <div className="flex justify-end">
              <button className="btn-primary" onClick={submit} disabled={busy} data-testid="ref-submit-button">
                <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
